'use client';

import { useEffect, useState } from 'react';
import { GrowthBook, GrowthBookProvider as GBProvider } from '@growthbook/growthbook-react';
import { GrowthBookAttributes, PlanType } from '@/lib/config/growthbook-features.config';

interface GrowthBookProviderProps {
  children: React.ReactNode;
  userId?: string;
  domainId?: string;
  planType?: PlanType;
  seats?: number;
  seatsUsed?: number;
  credits?: number;
  isOwner?: boolean;
  userRole?: 'owner' | 'admin' | 'contributor' | 'byline' | 'viewer';
  isLoadingSubscription?: boolean;
}

// Create singleton GrowthBook instance
let gb: GrowthBook | null = null;

function getGrowthBookInstance(): GrowthBook {
  if (!gb) {
    // Use the actual client key if available, otherwise skip GrowthBook entirely
    const apiHost = process.env.NEXT_PUBLIC_GROWTHBOOK_API_HOST || 'https://cdn.growthbook.io';
    const clientKey = process.env.NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY;

    // Only use demo-key in development, not in production
    const effectiveClientKey = clientKey || (process.env.NODE_ENV === 'development' ? 'demo-key' : null);
    
    gb = new GrowthBook({
      apiHost,
      clientKey: effectiveClientKey || '',
      enableDevMode: process.env.NODE_ENV === 'development',
      // Enable visual editor in development
      navigateDelay: process.env.NODE_ENV === 'development' ? 100 : 0,
      // Track impressions for analytics
      trackingCallback: (experiment, result) => {
        console.log('[GrowthBook] Feature viewed:', {
          experimentKey: experiment.key,
          variationId: result.key,
          attributes: gb?.getAttributes(),
        });
      },
    });
  }
  return gb;
}

export function GrowthBookProvider({
  children,
  userId = 'anonymous',
  domainId = 'default',
  planType = 'free',
  seats = 1,
  seatsUsed = 1,
  credits = 0,
  isOwner = false,
  userRole = 'viewer',
  isLoadingSubscription = false,
}: GrowthBookProviderProps) {
  const [growthbook] = useState(() => getGrowthBookInstance());
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Set user attributes for feature evaluation
    // isLoadingSubscription is used by BlurredMomentWrapper to avoid showing blur during load
    const attributes: GrowthBookAttributes & { isLoadingSubscription?: boolean } = {
      userId,
      domainId,
      planType,
      seats,
      seatsUsed,
      credits,
      isOwner,
      userRole,
      isLoadingSubscription,
    };

    growthbook.setAttributes(attributes);
    
    // Log current attributes in development
    if (process.env.NODE_ENV === 'development') {
      console.log('[GrowthBook] Attributes set:', attributes);
    }

    // Load features from GrowthBook (or use defaults in demo mode)
    const clientKey = process.env.NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY;
    const isDemo = !clientKey || clientKey === 'demo-key';
    
    console.log('[GrowthBook] Environment check:', {
      hasClientKey: !!clientKey,
      clientKey: clientKey ? `${clientKey.substring(0, 10)}...` : 'none',
      isDemo,
      planType,
      attributes
    });

    if (!clientKey || isDemo) {
      // Don't try to load from API if no key, just use demo features
      setIsReady(true);
      const demoFeatures = getDemoFeatures(planType);
      growthbook.setFeatures(demoFeatures);
      console.log('[GrowthBook] ✅ Demo mode - Features set based on plan:', planType);
      console.log('[GrowthBook] 🎯 moments_export_enabled:', demoFeatures.moments_export_enabled);
      console.log('[GrowthBook] 📦 All features:', Object.keys(demoFeatures).filter(k => k.includes('moment')));
    } else {
      // Load real features from GrowthBook API
      growthbook.loadFeatures().then(() => {
        setIsReady(true);
      }).catch((error) => {
        console.error('[GrowthBook] Failed to load features:', error);
        // Fall back to demo features on error
        const demoFeatures = getDemoFeatures(planType);
        growthbook.setFeatures(demoFeatures);
        console.log('[GrowthBook] Fallback to demo features due to error');
        setIsReady(true);
      });
    }

    return () => {
      growthbook.destroy();
    };
  }, [growthbook, userId, domainId, planType, seats, seatsUsed, credits, isOwner, userRole, isLoadingSubscription]);

  // Always provide the GrowthBook context, even while loading
  // This prevents "Missing or invalid GrowthBookProvider" errors
  return <GBProvider growthbook={growthbook}>{children}</GBProvider>;
}

// Demo features for local development without GrowthBook account
function getDemoFeatures(planType: PlanType) {
  const features: Record<string, boolean> = {
    // Visibility features based on plan
    visibility_public: true, // Everyone gets public
    visibility_domain_only: planType === 'pro' || planType === 'enterprise',
    visibility_always_private: planType === 'pro' || planType === 'enterprise',

    // Publishing features
    schedule_posts: planType !== 'free',
    manage_comments: planType !== 'free',
    social_sharing: planType === 'pro' || planType === 'enterprise',

    // Team features
    team_members: planType !== 'free',
    byline_authors: planType === 'pro' || planType === 'enterprise',

    // Content features
    unlimited_posts: planType !== 'free',
    video_4k_export: planType === 'pro' || planType === 'enterprise',
    bulk_operations: planType === 'pro' || planType === 'enterprise',

    // AI features
    ai_enhancements: planType !== 'free',

    // Advanced features
    custom_branding: planType === 'pro' || planType === 'enterprise',
    api_access: planType === 'pro' || planType === 'enterprise',
    advanced_analytics: planType === 'pro' || planType === 'enterprise',
    priority_support: planType === 'pro' || planType === 'enterprise',

    // Transcript features
    transcript_editing: planType !== 'free', // Available for plus, pro, enterprise
    unified_transcript_editor: true, // New unified editor (enabled for all during rollout)

    // Moment features
    moments_clear_view_limit: planType === 'free' ? 3 : planType === 'plus' ? 10 : -1,
    moments_blur_locked: planType === 'free' || planType === 'plus',
    moments_export_enabled: planType !== 'free',
    moments_preview_on_hover: planType !== 'free',
    moments_show_total_count: true, // All plans see total count
    moments_export_quality: planType === 'pro' || planType === 'enterprise' ? '4k' : planType === 'plus' ? '1080p' : '480p',
  };

  // Convert to GrowthBook feature format
  const gbFeatures: Record<string, { defaultValue: boolean }> = {};
  for (const [key, value] of Object.entries(features)) {
    gbFeatures[key] = { defaultValue: value };
  }

  return gbFeatures;
}